import { useCallback, useEffect, useMemo, useState } from "react";
import { FileSpreadsheet, FileText, Folder, Loader2, RotateCcw, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/FilterBar";
import { FileRow, FolderRow, tailleFr } from "@/components/FinderRows";
import { FinderEntetes, trierFichiers, useLargeurs } from "@/components/FinderEntetes";
import { ApercuFichier } from "@/components/ApercuFichier";
import { useFilters } from "@/context/FilterContext";
import { api, download, errMsg, telechargerExcel, telechargerPdf } from "@/lib/api";

const DOSSIERS = [
  { id: "airbnb", nom: "relevés Airbnb" },
  { id: "booking", nom: "relevés Booking" },
  { id: "desjardins", nom: "relevés Desjardins" },
  { id: "lodgify", nom: "relevés Lodgify" },
  { id: "rapprochements", nom: "Rapprochements" },
  { id: "preuves_booking", nom: "Preuves Booking" },
];
const SANS_DONNEES = ["rapprochement", "preuve_booking"];

const Sidebar = ({ vue, setVue, compte }) => (
  <aside className="flex gap-1 overflow-x-auto border-b border-slate-200 bg-slate-100/80 p-3 sm:w-52 sm:shrink-0 sm:flex-col sm:border-b-0 sm:border-r" data-testid="finder-sidebar">
    <p className="hidden sm:block px-2 pb-1 text-[11px] font-semibold text-slate-400">Dossiers</p>
    {[{ id: "tous", nom: "Tous les fichiers" }, ...DOSSIERS].map((d) => (
      <button key={d.id} type="button" onClick={() => setVue(d.id)} data-testid={`finder-vue-${d.id}`}
        className={`flex shrink-0 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${vue === d.id ? "bg-slate-300/60 text-slate-900" : "text-slate-600 hover:bg-slate-200/70"}`}>
        <Folder className="h-4 w-4 shrink-0 fill-sky-400 text-sky-500" />
        <span className="whitespace-nowrap">{d.nom}</span>
        <span className="ml-auto pl-2 text-[11px] font-mono text-slate-400">{compte(d.id)}</span>
      </button>
    ))}
  </aside>
);

const ConfirmSuppression = ({ cibles, fichiers, busy, onCancel, onConfirm }) => {
  const choisis = fichiers.filter((f) => cibles?.includes(f.id));
  const lignes = choisis.reduce((s, f) => s + (SANS_DONNEES.includes(f.type) ? 0 : f.lignes || 0), 0);
  const seulementRapports = choisis.length > 0 && choisis.every((f) => SANS_DONNEES.includes(f.type));
  return (
    <AlertDialog open={!!cibles} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="fichiers-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer {choisis.length === 1 ? `« ${choisis[0].nom} »` : `${choisis.length} fichiers`} ?</AlertDialogTitle>
          <AlertDialogDescription>
            {seulementRapports
              ? "Seul le document enregistré est supprimé ; vos données ne changent pas (une preuve Booking ne sera plus jointe au bilan)."
              : `Le fichier et ses données (${lignes} ligne(s) : opérations, réservations ou factures) seront supprimés, ainsi que leurs rapprochements. Cette action est irréversible.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="fichiers-confirm-cancel">Annuler</AlertDialogCancel>
          <AlertDialogAction className="bg-red-600 hover:bg-red-700" disabled={busy} data-testid="fichiers-confirm-delete"
            onClick={(e) => { e.preventDefault(); onConfirm(); }}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Supprimer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const dateExcel = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function Fichiers() {
  const { refreshOptions } = useFilters();
  const [fichiers, setFichiers] = useState(null);
  const [q, setQ] = useState("");
  const [vue, setVue] = useState("tous");
  const [ouverts, setOuverts] = useState(new Set());
  const [sel, setSel] = useState(new Set());
  const [cibles, setCibles] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ouvert, setOuvert] = useState(null);
  const [selResas, setSelResas] = useState(new Set());
  const { largeurs, changer, gabarit, tri, trier, reinit, perso } = useLargeurs();

  const load = useCallback(() => {
    api.get("/fichiers").then((r) => { setFichiers(r.data); setSel(new Set()); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSel(new Set()); }, [q, vue]);

  const tous = fichiers || [];
  const filtres = useMemo(() => tous.filter((f) => f.nom.toLowerCase().includes(q.trim().toLowerCase())), [tous, q]);
  const arbre = useMemo(() => DOSSIERS.filter((d) => vue === "tous" || vue === d.id).map((d) => {
    const fs = trierFichiers(filtres.filter((f) => f.dossier === d.id), tri);
    const annees = [...new Set(fs.map((f) => f.annee))].sort().reverse();
    return { ...d, fichiers: fs, annees: annees.map((a) => ({ annee: a, fichiers: fs.filter((f) => f.annee === a) })) };
  }), [filtres, vue, tri]);

  const recherche = q.trim() !== "";
  const estOuvert = (k) => recherche || ouverts.has(k);
  const toggle = (k) => setOuverts((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const coche = (ids, v) => setSel((s) => { const n = new Set(s); ids.forEach((i) => (v ? n.add(i) : n.delete(i))); return n; });
  const compte = (id) => (id === "tous" ? tous.length : tous.filter((f) => f.dossier === id).length);
  const visibles = arbre.flatMap((d) => d.fichiers);

  const telecharger = (f) => download(`/fichiers/${f.id}/telecharger`, {}, f.nom).catch(async (e) => {
    try { toast.error(JSON.parse(await e.response.data.text()).detail); } catch { toast.error("Téléchargement impossible"); }
  });

  const exporterPdf = () => telechargerPdf("fichiers", donneesFichiers()).catch((e) => toast.error(errMsg(e)));
  const exporterExcel = () => telechargerExcel("fichiers", donneesFichiers()).catch((e) => toast.error(errMsg(e)));
  const donneesFichiers = () => ({
    colonnes: ["Dossier", "Année", "Nom", "Date", "Contenu (lignes)", "Taille (Ko)"],
    lignes: arbre.flatMap((d) => d.annees.flatMap((a) => a.fichiers.map((f) => [
      { v: d.nom }, { v: a.annee ? Number(a.annee) || a.annee : null, f: "int" }, { v: f.nom }, { v: dateExcel(f.importe_le) },
      { v: f.lignes ?? null, f: "int" }, { v: f.taille == null ? null : Math.round(f.taille / 102.4) / 10, f: "num" },
    ]))),
  });

  const supprimer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/fichiers/supprimer", { ids: cibles });
      toast.success(`${data.supprimes} fichier(s) supprimé(s)`);
      setCibles(null);
      load();
      refreshOptions?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const supprimerCopies = async () => {
    setBusy(true);
    try { const { data } = await api.post("/fichiers/fusionner-doublons"); toast.success(`${data.supprimes} copie(s) en double supprimée(s)`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const nbCopies = tous.filter((f) => f.copie).length;

  return (
    <div data-testid="fichiers-page">
      <PageHeader eyebrow="Serveur" title="Fichiers" />
      {nbCopies > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="fichiers-copies-banner">
          <span><b>{nbCopies} copie(s) en double</b> : le même fichier a été ré-importé sans ajouter de ligne. Seul l'original est gardé (ses données ne changent pas).</span>
          <button type="button" disabled={busy} onClick={supprimerCopies} className="ml-auto rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50" data-testid="fichiers-copies-supprimer">Supprimer les copies</button>
        </div>
      )}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_20px_50px_-20px_rgba(15,23,42,0.25)] rise" data-testid="finder-window">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex gap-1.5" aria-hidden>
            <span className="h-3 w-3 rounded-full bg-[#FF5F57]" /><span className="h-3 w-3 rounded-full bg-[#FEBC2E]" /><span className="h-3 w-3 rounded-full bg-[#28C840]" />
          </div>
          <h2 className="text-base font-semibold text-slate-800" data-testid="finder-title">Rapprochement MD inc</h2>
          {perso && (
            <button type="button" onClick={reinit} data-testid="fichiers-reinit-colonnes"
              className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-800">
              <RotateCcw className="h-3 w-3" />Réinitialiser les colonnes
            </button>
          )}
          <button type="button" onClick={exporterExcel} disabled={!visibles.length} data-testid="fichiers-export-excel" title="Télécharger la liste en Excel, dans l'ordre affiché"
            className={`${perso ? "" : "ml-auto"} inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-800 disabled:opacity-40`}>
            <FileSpreadsheet className="h-3 w-3 text-emerald-700" />Excel
          </button>
          <button type="button" onClick={exporterPdf} disabled={!visibles.length} data-testid="fichiers-export-pdf" title="Télécharger la liste en PDF pour impression"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-800 disabled:opacity-40">
            <FileText className="h-3 w-3 text-emerald-700" />PDF
          </button>
          <div className={`relative w-full sm:w-64`}>
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un fichier"
              className="h-8 w-full rounded-md border border-slate-200 bg-white pl-8 pr-7 text-sm outline-none transition-shadow focus:ring-2 focus:ring-sky-300" data-testid="fichiers-search-input" />
            {q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="fichiers-search-clear"><X className="h-3.5 w-3.5" /></button>}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row min-h-[420px]">
          <Sidebar vue={vue} setVue={setVue} compte={compte} />
          <div className="flex-1 min-w-0 flex flex-col">
            {sel.size > 0 && (
              <div className="flex flex-wrap items-center gap-3 border-b border-sky-100 bg-sky-50 px-4 py-2" data-testid="fichiers-selection-bar">
                <span className="text-sm text-sky-900" data-testid="fichiers-selection-count">{sel.size} sélectionné(s)</span>
                <Button variant="ghost" size="sm" onClick={() => setSel(new Set())} data-testid="fichiers-deselect">Désélectionner</Button>
                <Button size="sm" className="ml-auto bg-red-600 hover:bg-red-700" onClick={() => setCibles([...sel])} data-testid="fichiers-bulk-delete">
                  <Trash2 className="mr-1.5 h-4 w-4" />Supprimer ({sel.size})
                </Button>
              </div>
            )}
            <div className="flex-1 overflow-x-auto" style={{ "--cols": gabarit }}>
            <div className="sm:w-max sm:min-w-full">
            <FinderEntetes largeurs={largeurs} changer={changer} noms={visibles.map((f) => f.nom)} tri={tri} trier={trier} />
            <div className="p-2" data-testid="finder-tree">
              {fichiers === null ? (
                <div className="grid place-items-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : arbre.map((d) => (
                <div key={d.id}>
                  <FolderRow nom={d.nom} ids={d.fichiers.map((f) => f.id)} depth={0} ouvert={estOuvert(d.id)} onToggle={() => toggle(d.id)}
                    sel={sel} onCoche={coche} testId={`dossier-${d.id}`} />
                  {estOuvert(d.id) && (d.annees.length ? d.annees.map((a) => (
                    <div key={a.annee}>
                      <FolderRow nom={a.annee} ids={a.fichiers.map((f) => f.id)} depth={1} ouvert={estOuvert(`${d.id}/${a.annee}`)}
                        onToggle={() => toggle(`${d.id}/${a.annee}`)} sel={sel} onCoche={coche} testId={`dossier-${d.id}-${a.annee}`} />
                      {estOuvert(`${d.id}/${a.annee}`) && a.fichiers.map((f) => (
                        <FileRow key={f.id} f={f} sel={sel} onCoche={coche} onDownload={telecharger} onDelete={setCibles} onOpen={setOuvert} />
                      ))}
                    </div>
                  )) : <p className="py-1.5 pl-14 text-xs text-slate-400" data-testid={`dossier-${d.id}-vide`}>{recherche ? "Aucun fichier ne correspond" : "Dossier vide"}</p>)}
                </div>
              ))}
            </div>
            </div>
            </div>
            <div className="border-t border-slate-100 bg-slate-50 px-4 py-1.5 text-center text-[11px] text-slate-500" data-testid="finder-status">
              {visibles.length} fichier(s) · {tailleFr(visibles.reduce((s, f) => s + (f.taille || 0), 0))}
              {visibles.some((f) => f.taille == null) && (
                <span className="text-slate-400" data-testid="finder-status-inconnus"> · {visibles.filter((f) => f.taille == null).length} sans taille connue (à réimporter)</span>
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmSuppression cibles={cibles} fichiers={tous} busy={busy} onCancel={() => setCibles(null)} onConfirm={supprimer} />
      <ApercuFichier fichier={ouvert} onClose={() => setOuvert(null)} onDownload={telecharger} sel={selResas} setSel={setSelResas} />
    </div>
  );
}
